import imageCompression from 'browser-image-compression';

/**
 * Build the API URL for a stored image
 * @param {number|string|null} imageId - The image ID
 * @param {string|number} [version] - Optional cache-buster (typically a
 *   timestamp). Si se pasa, se agrega `?v=<version>` para forzar al browser
 *   a refetchear cuando el contenido del imageId cambia (ej. tras subir
 *   una nueva foto de perfil que reusa el mismo ID).
 * @returns {string|null} - The image URL, or null if no imageId
 */
export const getImageUrl = (imageId, version) => {
  if (!imageId) return null;
  const base = `/api/images/${imageId}`;
  return version != null ? `${base}?v=${encodeURIComponent(version)}` : base;
};

/**
 * Resize an image maintaining aspect ratio
 * @param {File} file - The image file to resize
 * @param {number} maxWidth - Maximum width in pixels
 * @param {number} maxHeight - Maximum height in pixels
 * @returns {Promise<Blob>} - The resized image as a Blob
 */
export const resizeImage = (file, maxWidth, maxHeight) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions maintaining aspect ratio
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width *= maxHeight / height;
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            resolve(blob);
          },
          file.type,
          0.9
        );
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
};

/**
 * Calcula el bounding box de una imagen rotada (en grados).
 */
const rotatedBoundingBox = (width, height, rotationDeg) => {
  const rad = getRadianAngle(rotationDeg);
  return {
    width: Math.abs(Math.cos(rad) * width) + Math.abs(Math.sin(rad) * height),
    height: Math.abs(Math.sin(rad) * width) + Math.abs(Math.cos(rad) * height),
  };
};

/**
 * Crop an image with optional rotation. Sigue el patrón canónico de
 * react-easy-crop: dibuja la imagen rotada en un canvas del tamaño del
 * bounding box rotado, y recorta con coordenadas en el espacio de la
 * imagen rotada (que es el espacio en el que pixelCrop viene).
 *
 * El bug previo usaba image.width/height (dimensiones sin rotar) para
 * posicionar el crop, pero pixelCrop está en coordenadas del frame
 * rotado, así que la región final no matcheaba la previsualización.
 *
 * @param {string} imageSrc - The image source (base64 or URL)
 * @param {Object} pixelCrop - The crop area in pixels (rotated frame)
 * @param {number} rotation - Rotation in degrees
 * @returns {Promise<Blob>} - The cropped image as a Blob
 */
export const getCroppedImg = async (imageSrc, pixelCrop, rotation = 0) => {
  const image = await createImage(imageSrc);
  const rotatedSize = rotatedBoundingBox(image.width, image.height, rotation);

  const rotatedCanvas = document.createElement('canvas');
  const rctx = rotatedCanvas.getContext('2d');
  rotatedCanvas.width = rotatedSize.width;
  rotatedCanvas.height = rotatedSize.height;

  // Centramos para rotar alrededor del centro y luego dibujamos la imagen
  // con su origen offseteado a la esquina sup-izq.
  rctx.translate(rotatedSize.width / 2, rotatedSize.height / 2);
  rctx.rotate(getRadianAngle(rotation));
  rctx.translate(-image.width / 2, -image.height / 2);
  rctx.drawImage(image, 0, 0);

  const cropped = document.createElement('canvas');
  const cctx = cropped.getContext('2d');
  cropped.width = pixelCrop.width;
  cropped.height = pixelCrop.height;

  cctx.drawImage(
    rotatedCanvas,
    pixelCrop.x, pixelCrop.y,
    pixelCrop.width, pixelCrop.height,
    0, 0,
    pixelCrop.width, pixelCrop.height,
  );

  return new Promise((resolve) => {
    cropped.toBlob(
      (blob) => {
        resolve(blob);
      },
      'image/jpeg',
      0.9,
    );
  });
};

/**
 * Compress an image file
 * @param {File} imageFile - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - The compressed image file
 */
export const compressImage = async (imageFile, options = {}) => {
  const defaultOptions = {
    maxSizeMB: 1,
    maxWidthOrHeight: 1920,
    useWebWorker: true,
    ...options
  };

  try {
    const compressedFile = await imageCompression(imageFile, defaultOptions);
    return compressedFile;
  } catch (error) {
    // Silently ignore
    throw error;
  }
};

/**
 * Get file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Create an image element from source
 * @param {string} url - Image URL or base64
 * @returns {Promise<HTMLImageElement>}
 */
const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

/**
 * Convert degrees to radians
 * @param {number} degreeValue - Value in degrees
 * @returns {number} - Value in radians
 */
const getRadianAngle = (degreeValue) => {
  return (degreeValue * Math.PI) / 180;
};

/**
 * Read file as data URL
 * @param {File} file - File to read
 * @returns {Promise<string>} - Base64 data URL
 */
export const readFile = (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener('load', () => resolve(reader.result));
    reader.readAsDataURL(file);
  });
};

/**
 * Validate image file
 * @param {File} file - File to validate
 * @returns {Object} - Validation result
 */
export const validateImageFile = (file) => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  // Alineado con backend: services/.../ImageValidator.MAX_FILE_SIZE = 5MB
  const maxSize = 5 * 1024 * 1024;

  if (!validTypes.includes(file.type)) {
    return {
      valid: false,
      error: 'Invalid file type. Please upload an image file (JPEG, PNG, GIF, or WebP).'
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: `File size exceeds 5MB. Current size: ${formatFileSize(file.size)}`
    };
  }

  return { valid: true };
};

/**
 * Check if an image is square (1:1 aspect ratio)
 * @param {HTMLImageElement|string} imageSource - Image element or URL/base64
 * @returns {Promise<Object>} - Analysis result with dimensions and isSquare flag
 */
export const analyzeImageAspectRatio = async (imageSource) => {
  const img = typeof imageSource === 'string' ? await createImage(imageSource) : imageSource;
  const aspectRatio = img.width / img.height;
  const tolerance = 0.02; // Allow 2% tolerance for "almost square"
  
  return {
    width: img.width,
    height: img.height,
    aspectRatio,
    isSquare: Math.abs(aspectRatio - 1) < tolerance,
    orientation: aspectRatio > 1 ? 'landscape' : aspectRatio < 1 ? 'portrait' : 'square'
  };
};

/**
 * Detect edge colors of an image
 * @param {string} imageSource - Image URL or base64
 * @param {number} sampleSize - Number of pixels to sample from each edge
 * @returns {Promise<Object>} - Edge colors and analysis
 */
export const detectEdgeColors = async (imageSource, sampleSize = 20) => {
  const img = await createImage(imageSource);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  canvas.width = img.width;
  canvas.height = img.height;
  ctx.drawImage(img, 0, 0);
  
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { width, height, data } = imageData;
  
  // Sample pixels from each edge
  const edges = {
    top: [],
    right: [],
    bottom: [],
    left: []
  };
  
  // Sample top edge
  for (let x = 0; x < width; x += Math.floor(width / sampleSize)) {
    const idx = (0 * width + x) * 4;
    edges.top.push({ r: data[idx], g: data[idx + 1], b: data[idx + 2] });
  }
  
  // Sample right edge
  for (let y = 0; y < height; y += Math.floor(height / sampleSize)) {
    const idx = (y * width + (width - 1)) * 4;
    edges.right.push({ r: data[idx], g: data[idx + 1], b: data[idx + 2] });
  }
  
  // Sample bottom edge
  for (let x = 0; x < width; x += Math.floor(width / sampleSize)) {
    const idx = ((height - 1) * width + x) * 4;
    edges.bottom.push({ r: data[idx], g: data[idx + 1], b: data[idx + 2] });
  }
  
  // Sample left edge
  for (let y = 0; y < height; y += Math.floor(height / sampleSize)) {
    const idx = (y * width + 0) * 4;
    edges.left.push({ r: data[idx], g: data[idx + 1], b: data[idx + 2] });
  }
  
  // Calculate average color for each edge and check uniformity
  const averageColors = {};
  const edgeUniformity = {};
  
  for (const [edge, colors] of Object.entries(edges)) {
    const avg = colors.reduce((acc, color) => ({
      r: acc.r + color.r / colors.length,
      g: acc.g + color.g / colors.length,
      b: acc.b + color.b / colors.length
    }), { r: 0, g: 0, b: 0 });
    
    averageColors[edge] = {
      r: Math.round(avg.r),
      g: Math.round(avg.g),
      b: Math.round(avg.b)
    };
    
    // Check if colors within this edge are uniform
    let maxVariance = 0;
    for (const color of colors) {
      const variance = Math.sqrt(
        Math.pow(color.r - avg.r, 2) +
        Math.pow(color.g - avg.g, 2) +
        Math.pow(color.b - avg.b, 2)
      );
      maxVariance = Math.max(maxVariance, variance);
    }
    edgeUniformity[edge] = maxVariance;
  }
  
  // Check if all edges are similar
  const allColors = Object.values(averageColors);
  const avgOverall = allColors.reduce((acc, color) => ({
    r: acc.r + color.r / allColors.length,
    g: acc.g + color.g / allColors.length,
    b: acc.b + color.b / allColors.length
  }), { r: 0, g: 0, b: 0 });
  
  const overallAverage = {
    r: Math.round(avgOverall.r),
    g: Math.round(avgOverall.g),
    b: Math.round(avgOverall.b)
  };
  
  // Check if edges match - be very strict
  const threshold = 5; // Very strict color difference threshold (was 30)
  const uniformityThreshold = 8; // Maximum variance within each edge
  
  // First check if each edge is uniform within itself
  const allEdgesUniform = Object.values(edgeUniformity).every(variance => variance < uniformityThreshold);
  
  // Then check if all edge colors are very similar to each other
  let maxDifference = 0;
  for (let i = 0; i < allColors.length; i++) {
    for (let j = i + 1; j < allColors.length; j++) {
      const diff = Math.sqrt(
        Math.pow(allColors[i].r - allColors[j].r, 2) +
        Math.pow(allColors[i].g - allColors[j].g, 2) +
        Math.pow(allColors[i].b - allColors[j].b, 2)
      );
      maxDifference = Math.max(maxDifference, diff);
    }
  }
  
  // Only consider edges matching if:
  // 1. Each edge is uniform within itself
  // 2. All edges are nearly identical to each other
  const edgesMatch = allEdgesUniform && maxDifference < threshold;
  
  // Check if color is white or near-white
  const isWhiteBackground = overallAverage.r > 240 && 
                          overallAverage.g > 240 && 
                          overallAverage.b > 240;
  
  return {
    edges: averageColors,
    overallAverage,
    edgesMatch,
    isWhiteBackground,
    hex: rgbToHex(overallAverage.r, overallAverage.g, overallAverage.b)
  };
};

/**
 * Check if two colors are similar
 * @param {Object} color1 - First color {r, g, b}
 * @param {Object} color2 - Second color {r, g, b}
 * @param {number} threshold - Maximum allowed difference
 * @returns {boolean} - True if colors are similar
 */
export const areColorsSimilar = (color1, color2, threshold = 30) => {
  const diff = Math.sqrt(
    Math.pow(color1.r - color2.r, 2) +
    Math.pow(color1.g - color2.g, 2) +
    Math.pow(color1.b - color2.b, 2)
  );
  return diff < threshold;
};

/**
 * Convert RGB to hex color
 * @param {number} r - Red value
 * @param {number} g - Green value
 * @param {number} b - Blue value
 * @returns {string} - Hex color string
 */
const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
};

/**
 * Pad image to square with background color
 * @param {string} imageSource - Image URL or base64
 * @param {string} backgroundColor - Background color (hex or rgb)
 * @param {string} outputType - Output image MIME type (default: 'image/jpeg')
 * @returns {Promise<Blob>} - Padded square image as blob
 */
export const padImageToSquare = async (imageSource, backgroundColor = '#ffffff', outputType = 'image/jpeg', forCircle = false) => {
  const img = await createImage(imageSource);
  const maxDimension = Math.max(img.width, img.height);

  // For circular display, add extra padding so the image fits inside the inscribed circle.
  // A circle inscribed in a square has diameter = side, so content at the edges gets clipped.
  // Scale factor ~0.70 (1/√2) keeps content within the circle bounds.
  const canvasSize = forCircle ? Math.ceil(maxDimension * 1.42) : maxDimension;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = canvasSize;
  canvas.height = canvasSize;

  // Fill background
  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, canvasSize, canvasSize);

  // Center the image
  const x = (canvasSize - img.width) / 2;
  const y = (canvasSize - img.height) / 2;
  ctx.drawImage(img, x, y);
  
  // Determine quality based on format
  const quality = outputType === 'image/png' ? undefined : 0.95;
  
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => resolve(blob),
      outputType,
      quality
    );
  });
};